// dependencies
import { Chart } from "chart.js";
import { format } from "date-fns";
import { RefObject, useMemo } from "react";

// config
import Config from "./Config";

// store
import useStore from "../../../../../store";
// schema
import { ChartType } from "../../../../../store/enums";

// hooks
import { useCurrentUser, useRbacPrimaryRolePermissions, useUser } from "../../../../../../../../../api/hooks";

import avatarURL from "../../../../../../../../../utils/avatarURL";
import { getColor } from "../../../../../../../../../utils/getColor";
import { canCreateNewReviewReport, getShouldDisplayReviewsInProgressMessage } from "../../../../../utils";
import { getAvatarImage } from "../ScatterChart/utils";
import { truncateText } from "../utils";

import { avatarCache } from "../../shared/avatarCache";
import BarChartConfig from "../BarChart/Config";
import useChartOptions from "../UseChartOptions/useChartOptions";
import preloadImages from "../preloadImages";

type LegendInfo = {
	description?: string;
	yAxisLabel: string;
	xAxisLabel: string;
	reportName: string;
	currentUser: { id: string; name: string };
	filterCount: number;
	chartType: ChartType;
	visualizeOptions: { [key: string]: string };
};

type ChartMetaData = {
	chartType: ChartType;
	description?: string;
	xAxisLabel: string;
	yAxisLabel?: string;
};

const dataSeriesRangeOptions = {
	TOP_5: "Top 5",
	TOP_10: "Top 10",
	TOP_25: "Top 25",
	TOP_50: "Top 50",
	TOP_100: "Top 100",
	TOP_200: "Top 200",
	BOTTOM_5: "Bottom 5",
	BOTTOM_10: "Bottom 10",
	BOTTOM_25: "Bottom 25",
	BOTTOM_50: "Bottom 50",
	BOTTOM_100: "Bottom 100",
	BOTTOM_200: "Bottom 200",
	month: "Monthly",
	year: "Yearly",
	quarter: "Quarterly",
};

/**
 * A hook that can be used to provide a the function
 * download a chart as an image.
 *
 * @param chartRef - a reference to the chart to be downloaded
 * @param titleRef - a reference to the title of the chart to be downloaded
 * @param metaData - the metadata to be used in the legend
 * @returns an object containing the downloadImage function and the legendInfo
 */
export const useDownloadImage = (chartRef: RefObject<Chart>, metaData: ChartMetaData) => {
	const { data: authData, isLoading } = useCurrentUser();
	const { data: permissions } = useRbacPrimaryRolePermissions("report_analytics");
	const { data: currentUser } = useUser({
		userID: authData?.me?.authorization?.payload?.user_id || "",
		disabled: !authData?.me?.authorization?.payload?.user_id,
	});
	preloadImages([avatarURL(authData?.me?.authorization?.payload?.user_id || "")], isLoading);
	// const { description, chartType, xAxisLabel, yAxisLabel } = metaData;
	const description = metaData?.description;
	const chartType = metaData?.chartType;
	const xAxisLabel = metaData?.xAxisLabel;
	const yAxisLabel = metaData?.yAxisLabel;
	const [
		report,
		uniqueReviewReportQuestions,
		enabledSummarizeFields,
		groupedQuestions,
		selectedQuickFilter,
		appliedFiltersCount,
		getEnabledGridColumns,
		getAppliedFilters,
	] = useStore((state) => [
		state.report,
		state.uniqueReviewReportQuestions,
		state.enabledSummarizeFields,
		state.groupedQuestions,
		state.selectedQuickFilter,
		state.appliedFiltersCount,
		state.getEnabledGridColumns,
		state.getAppliedFilters,
	]);
	const { visualizeOption } = useChartOptions(chartType);
	const filters = getAppliedFilters();
	const shouldDisplayReviewsInProgressMessage = getShouldDisplayReviewsInProgressMessage(
		canCreateNewReviewReport(permissions),
		filters,
		report?.type
	);

	const legendInfo: LegendInfo = useMemo(() => {
		const formattedVisualizeOptions = Object.keys(visualizeOption).reduce((acc, key) => {
			let columnValue = visualizeOption[key]?.column || "";
			if (columnValue.startsWith("question_")) {
				// questions have to be looked up in the uniqueReviewReportQuestions array
				const questionID = columnValue.split("_")[1];
				columnValue = uniqueReviewReportQuestions.find((question) => question._id === questionID)?.question;
			} else if (columnValue.startsWith("groupedquestion_")) {
				// grouped questions have to be looked up in the groupedQuestions array
				columnValue = groupedQuestions.find((question) => question.id === columnValue.split("_")[1])?.label;
			} else if (columnValue.startsWith("summary_")) {
				// remove the string "summary_" and look up the column in the enabledSummarizeFields array
				columnValue = enabledSummarizeFields.find((field) => field.value === columnValue.slice(8))?.label;
			} else {
				const columns = getEnabledGridColumns();
				columnValue = columns.find((column) => column.field === columnValue)?.headerName;
			}
			if (key === "dataSeries") {
				const dataSeriesTextPrefix =
					chartType === ChartType.HistogramChart
						? ""
						: (dataSeriesRangeOptions[visualizeOption[key]?.rangeOption] || "Unknown") + " ";
				return {
					...acc,
					[key]: columnValue,
					dataSeriesText: `${dataSeriesTextPrefix}${columnValue} Items`,
				};
			}
			return {
				...acc,
				[key]: columnValue,
			};
		}, {});

		return {
			description,
			yAxisLabel: yAxisLabel || "",
			xAxisLabel,
			chartType,
			currentUser: {
				id: authData?.me?.authorization?.payload?.user_id || "",
				name: currentUser?.name || "",
			},
			filterCount: appliedFiltersCount || 0,
			reportName: report?.name || "",
			visualizeOptions: formattedVisualizeOptions,
		};
	}, [
		JSON.stringify(report),
		JSON.stringify(currentUser),
		description,
		visualizeOption,
		uniqueReviewReportQuestions,
		groupedQuestions,
		enabledSummarizeFields,
		selectedQuickFilter,
	]);

	// creates the title text for the exported image
	const formatTitleText = (ctx: CanvasRenderingContext2D) => {
		const dataSeries = legendInfo.visualizeOptions["dataSeries"];
		const xAxis = legendInfo.visualizeOptions["xAxis"];
		const yAxis = legendInfo.visualizeOptions["yAxis"];
		const baseTruncationWidth = (Config.EXPORT_WIDTH - Config.OUTER_PADDING * 2 - Config.INNER_PADDING * 2) * 0.7;
		if (yAxis?.length && xAxis?.length) {
			const truncationWidth = baseTruncationWidth / 3;
			return `${truncateText(ctx, xAxis, truncationWidth)} and ${truncateText(
				ctx,
				yAxis,
				truncationWidth
			)} by ${truncateText(ctx, dataSeries, truncationWidth)}`;
		} else if (xAxis?.length) {
			const truncationWidth = baseTruncationWidth / 2;
			if (chartType === ChartType.HistogramChart) {
				const prefix = "Distribution of";
				return `${prefix} ${truncateText(ctx, dataSeries, truncationWidth)} by ${truncateText(
					ctx,
					xAxis,
					truncationWidth
				)}`;
			}
			return `${truncateText(ctx, xAxis, truncationWidth)} by ${truncateText(ctx, dataSeries, truncationWidth)}`;
		} else if (yAxis?.length) {
			const truncationWidth = baseTruncationWidth / 2;
			return `${truncateText(ctx, yAxis, truncationWidth)} by ${truncateText(ctx, dataSeries, truncationWidth)}`;
		}
		return truncateText(ctx, dataSeries, baseTruncationWidth);
	};

	// splits the metrics text onto two lines and truncates line 2 if necessary
	const formatMetricsText = (legendCtx: CanvasRenderingContext2D, metricsLabelWidth: number) => {
		let lineOne: string = "";
		let lineTwo: string = "";
		let lineOneComplete = false;
		let metricsText = "";
		const dataSeries = legendInfo.visualizeOptions["dataSeries"];
		const xAxis = legendInfo.visualizeOptions["xAxis"];
		const yAxis = legendInfo.visualizeOptions["yAxis"];
		if (yAxis?.length && xAxis?.length) {
			metricsText = `${xAxis} and ${yAxis} by ${dataSeries}`;
		} else if (xAxis?.length) {
			metricsText = `${xAxis} by ${dataSeries}`;
		} else if (yAxis?.length) {
			metricsText = `${yAxis} by ${dataSeries}`;
		}
		metricsText.split(" ").forEach((word) => {
			if (!lineOneComplete) {
				const { width: lineWidth } = legendCtx.measureText(lineOne + word);
				if (
					lineWidth + metricsLabelWidth + Config.INNER_GAP + Config.INNER_PADDING >
					Config.LEFT_COLUMN_WIDTH
				) {
					lineOneComplete = true;
					lineTwo = lineTwo.length ? `${lineTwo} ${word}` : word;
				} else {
					lineOne = lineOne.length ? `${lineOne} ${word}` : word;
				}
			} else {
				lineTwo = lineTwo.length ? `${lineTwo} ${word}` : word;
			}
		});
		if (lineTwo.length) {
			lineTwo = truncateText(legendCtx, lineTwo, Config.LEFT_COLUMN_WIDTH - Config.INNER_PADDING);
		}
		return [lineOne, lineTwo];
	};

	// splits the data series text onto two lines and truncates line 2 if necessary
	const formatDataSeriesText = (
		legendCtx: CanvasRenderingContext2D,
		dataSeriesLabelWidth: number
	): [string, string] => {
		let lineOne: string = "";
		let lineTwo: string = "";
		let lineOneComplete = false;
		const dataSeriesText = legendInfo.visualizeOptions["dataSeriesText"] || "";
		dataSeriesText.split(" ").forEach((word: string) => {
			if (!lineOneComplete) {
				const { width: lineWidth } = legendCtx.measureText(lineOne + word);
				if (
					lineWidth + dataSeriesLabelWidth + Config.INNER_GAP + Config.INNER_PADDING >
					Config.LEFT_COLUMN_WIDTH
				) {
					lineOneComplete = true;
					lineTwo = lineTwo.length ? `${lineTwo} ${word}` : word;
				} else {
					lineOne = lineOne.length ? `${lineOne} ${word}` : word;
				}
			} else {
				lineTwo = lineTwo.length ? `${lineTwo} ${word}` : word;
			}
		});
		if (lineTwo.length) {
			lineTwo = truncateText(legendCtx, lineTwo, Config.LEFT_COLUMN_WIDTH - Config.INNER_PADDING);
		}
		return [lineOne, lineTwo];
	};

	// formats the downloaded text, splitting onto two lines if necessary
	const formatDownloadedText = (
		legendCtx: CanvasRenderingContext2D,
		downloadedLabelWidth: number,
		legendWidth: number
	): { lines: number; dateLine: string; userLine: string } => {
		const dateText = `${format(new Date(), "MMM dd, yyyy")} • ${format(new Date(), "h:mmaaa")} by`;
		const { width: dateTextWidth } = legendCtx.measureText(dateText);
		const availableWidth = legendWidth - Config.LEFT_COLUMN_WIDTH - Config.INNER_PADDING * 2;
		const usernameStartX =
			downloadedLabelWidth + Config.INNER_GAP + dateTextWidth + Config.AVATAR_DIAMETER + Config.INNER_GAP / 2;

		// Check if everything fits on one line
		const { width: usernameWidth } = legendCtx.measureText(legendInfo.currentUser.name);
		if (usernameStartX + usernameWidth <= availableWidth) {
			return { lines: 1, dateLine: dateText, userLine: legendInfo.currentUser.name };
		}

		// Split onto two lines: date on line 1, avatar+username on line 2
		const dateOnly = `${format(new Date(), "MMM dd, yyyy")} • ${format(new Date(), "h:mmaaa")}`;
		const truncatedUsername = truncateText(
			legendCtx,
			legendInfo.currentUser.name,
			availableWidth - Config.AVATAR_DIAMETER - Config.INNER_GAP / 2
		);
		return { lines: 2, dateLine: dateOnly, userLine: truncatedUsername };
	};

	const formatReportName = (
		ctx: CanvasRenderingContext2D,
		reportName: string,
		reportTitleWidth: number,
		availableWidth: number
	): string[] => {
		const reportNameLines: string[] = [];
		const words = reportName.split(" ");
		let lineOne = "";
		let lineTwo = "";
		let lineOneComplete = false;
		words.forEach((word) => {
			if (!lineOneComplete) {
				const { width: lineWidth } = ctx.measureText(lineOne + word);
				if (lineWidth + reportTitleWidth + Config.INNER_GAP > availableWidth) {
					lineOneComplete = true;
					lineTwo = lineTwo.length ? `${lineTwo} ${word}` : word;
				} else {
					lineOne = lineOne.length ? `${lineOne} ${word}` : word;
				}
			} else {
				lineTwo = lineTwo.length ? `${lineTwo} ${word}` : word;
			}
		});
		reportNameLines.push(lineOne);
		if (lineTwo.length) {
			reportNameLines.push(truncateText(ctx, lineTwo, availableWidth));
		}
		return reportNameLines;
	};

	const getAndDownscaleAvatarImage = (avatarSrc: string) => {
		let avatarImage = getAvatarImage(avatarSrc, avatarCache);
		while (avatarImage.width > Config.AVATAR_DIAMETER * 2) {
			const canvas = document.createElement("canvas");
			canvas.width = avatarImage.width * 0.9;
			canvas.height = avatarImage.height * 0.9;
			const ctx = canvas.getContext("2d");
			if (ctx) {
				ctx.imageSmoothingQuality = "high";
				ctx.drawImage(avatarImage, 0, 0, canvas.width, canvas.height);
				avatarImage = canvas;
			}
		}
		return avatarImage;
	};

	const createLegend = (legendWidth: number): HTMLCanvasElement => {
		const legend = document.createElement("canvas");
		const legendCtx = legend.getContext("2d");
		if (legendCtx) {
			const semiBoldText = `600 ${Config.FONT_SIZE}px Source Sans Pro, sans-serif`;
			const regularText = `400 ${Config.FONT_SIZE}px Source Sans Pro, sans-serif`;
			const captionText = `400 ${Config.FONT_SIZE - 2}px Source Sans Pro, sans-serif`;

			// needed initially to measure height ( 1 vs 2 lines for metrics, data series, downloaded )
			legendCtx.font = semiBoldText;
			const { width: metricsLabelWidth } = legendCtx.measureText("Metrics:");
			const metricsTextLines = formatMetricsText(legendCtx, metricsLabelWidth);
			const { width: dataSeriesLabelWidth } = legendCtx.measureText("Data Series:");
			const dataSeriesTextLines = formatDataSeriesText(legendCtx, dataSeriesLabelWidth);
			const { width: downloadedLabelWidth } = legendCtx.measureText("Downloaded:");
			const downloadedTextInfo = formatDownloadedText(legendCtx, downloadedLabelWidth, legendWidth);

			// rounded rectangle for the legend
			legend.width = legendWidth;
			legend.height =
				Config.INNER_PADDING * 2 +
				Config.LINE_HEIGHT * 3 +
				Config.INNER_GAP * 2 -
				(Config.LINE_HEIGHT - Config.FONT_SIZE);
			if (metricsTextLines[1].length) {
				// extra line if needed for metrics
				legend.height += Config.LINE_HEIGHT;
			}
			if (dataSeriesTextLines[1].length) {
				// extra line if needed for data series
				legend.height += Config.LINE_HEIGHT;
			}
			if (downloadedTextInfo.lines === 2) {
				// extra line if needed for downloaded
				legend.height += Config.LINE_HEIGHT;
			}

			if (shouldDisplayReviewsInProgressMessage) {
				legend.height += Config.LINE_HEIGHT;
			}

			legendCtx.beginPath();
			legendCtx.roundRect(0, 0, legend.width, legend.height, Config.BORDER_RADIUS);
			legendCtx.fillStyle = getColor("neutral", 1);
			legendCtx.strokeStyle = getColor("neutral", 1);
			legendCtx.fill();
			legendCtx.stroke();

			// two columns of legend info
			// left column: Chart Type, Data Series, Metrics
			legendCtx.fillStyle = getColor("neutral", 9);
			legendCtx.textAlign = "start";
			legendCtx.textBaseline = "top";
			let yPosition = Config.INNER_PADDING;
			// Messages for chart that includes reviews in progress
			if (shouldDisplayReviewsInProgressMessage) {
				legendCtx.font = captionText;
				legendCtx.fillText(
					"**Results may include review forms still in progress",
					Config.INNER_PADDING,
					yPosition
				);
				yPosition += Config.LINE_HEIGHT + Config.INNER_GAP - 5;
			}

			// Chart Type:
			legendCtx.font = semiBoldText;
			const { width: chartTypeLabelWidth } = legendCtx.measureText("Chart Type:");
			legendCtx.fillText("Chart Type:", Config.INNER_PADDING, yPosition);
			legendCtx.font = regularText;
			legendCtx.fillText(chartType, chartTypeLabelWidth + Config.INNER_PADDING + Config.INNER_GAP, yPosition);
			yPosition += Config.LINE_HEIGHT + Config.INNER_GAP;

			// Data Series:
			legendCtx.font = semiBoldText;
			legendCtx.fillText("Data Series:", Config.INNER_PADDING, yPosition);
			legendCtx.font = regularText;
			legendCtx.fillText(
				dataSeriesTextLines[0],
				Config.INNER_PADDING + Config.INNER_GAP + dataSeriesLabelWidth,
				yPosition
			);
			if (dataSeriesTextLines[1].length) {
				yPosition += Config.LINE_HEIGHT + Config.INNER_GAP;
				legendCtx.fillText(dataSeriesTextLines[1], Config.INNER_PADDING, yPosition);
			}
			yPosition += Config.LINE_HEIGHT + Config.INNER_GAP;

			// Metrics
			legendCtx.font = semiBoldText;
			legendCtx.fillText("Metrics:", Config.INNER_PADDING, yPosition);
			legendCtx.font = regularText;
			legendCtx.fillText(
				metricsTextLines[0],
				metricsLabelWidth + Config.INNER_GAP + Config.INNER_PADDING,
				yPosition
			);
			if (metricsTextLines[1].length) {
				yPosition += Config.LINE_HEIGHT + Config.INNER_GAP;
				legendCtx.fillText(metricsTextLines[1], Config.INNER_PADDING, yPosition);
			}

			// right column: Filters (count), Report Title, Downloaded (Date/time by Avatar/Username)
			// Filters
			yPosition = Config.INNER_PADDING;
			if (shouldDisplayReviewsInProgressMessage) {
				yPosition += Config.LINE_HEIGHT + Config.INNER_GAP - 5;
			}

			legendCtx.font = semiBoldText;
			const { width: filtersLabelWidth } = legendCtx.measureText("Filters:");
			legendCtx.fillText("Filters:", Config.LEFT_COLUMN_WIDTH + Config.INNER_PADDING, yPosition);
			legendCtx.font = regularText;
			legendCtx.fillText(
				`${legendInfo.filterCount} Filter${legendInfo.filterCount === 1 ? "" : "s"} Applied`,
				Config.INNER_PADDING + Config.LEFT_COLUMN_WIDTH + filtersLabelWidth + Config.INNER_GAP,
				yPosition
			);
			yPosition += Config.LINE_HEIGHT + Config.INNER_GAP;

			// Report Title
			legendCtx.font = semiBoldText;
			const { width: reportTitleLabelWidth } = legendCtx.measureText("Report Title:");
			legendCtx.fillText("Report Title:", Config.LEFT_COLUMN_WIDTH + Config.INNER_PADDING, yPosition);
			legendCtx.font = regularText;
			const reportNameLines = formatReportName(
				legendCtx,
				legendInfo.reportName,
				reportTitleLabelWidth,
				legendWidth - Config.INNER_PADDING * 2 - Config.LEFT_COLUMN_WIDTH
			);
			legendCtx.fillText(
				reportNameLines[0],
				Config.LEFT_COLUMN_WIDTH + Config.INNER_PADDING + reportTitleLabelWidth + Config.INNER_GAP,
				yPosition
			);
			let additionalSpace = 0;

			if (reportNameLines[1]?.length) {
				// extra line if needed for report title
				additionalSpace += Config.LINE_HEIGHT;
				yPosition += Config.LINE_HEIGHT + Config.INNER_GAP;
				legendCtx.fillText(reportNameLines[1], Config.LEFT_COLUMN_WIDTH + Config.INNER_PADDING, yPosition);
			}
			yPosition += Config.LINE_HEIGHT + Config.INNER_GAP + additionalSpace;
			legendCtx.font = semiBoldText;
			legendCtx.fillText("Downloaded:", Config.LEFT_COLUMN_WIDTH + Config.INNER_PADDING, yPosition);
			legendCtx.font = regularText;

			// fetch the avatar of the current user
			const avatarSrc = avatarURL(legendInfo.currentUser.id);
			const avatarImage = getAndDownscaleAvatarImage(avatarSrc);

			if (downloadedTextInfo.lines === 1) {
				// Single line: date by avatar username
				const { width: dateTextWidth } = legendCtx.measureText(downloadedTextInfo.dateLine);
				legendCtx.fillText(
					downloadedTextInfo.dateLine,
					Config.LEFT_COLUMN_WIDTH + Config.INNER_PADDING + downloadedLabelWidth + Config.INNER_GAP,
					yPosition
				);
				legendCtx.drawImage(
					avatarImage,
					Config.LEFT_COLUMN_WIDTH +
						Config.INNER_PADDING +
						downloadedLabelWidth +
						Config.INNER_GAP +
						dateTextWidth,
					yPosition - 3,
					Config.AVATAR_DIAMETER,
					Config.AVATAR_DIAMETER
				);
				legendCtx.fillText(
					downloadedTextInfo.userLine,
					Config.LEFT_COLUMN_WIDTH +
						Config.INNER_PADDING +
						downloadedLabelWidth +
						Config.INNER_GAP +
						dateTextWidth +
						Config.INNER_GAP / 2 +
						Config.AVATAR_DIAMETER,
					yPosition
				);
			} else {
				// Two lines: date on line 1, avatar+username on line 2
				legendCtx.fillText(
					downloadedTextInfo.dateLine,
					Config.LEFT_COLUMN_WIDTH + Config.INNER_PADDING + downloadedLabelWidth + Config.INNER_GAP,
					yPosition
				);
				yPosition += Config.LINE_HEIGHT + Config.INNER_GAP;
				legendCtx.drawImage(
					avatarImage,
					Config.LEFT_COLUMN_WIDTH + Config.INNER_PADDING,
					yPosition - 3,
					Config.AVATAR_DIAMETER,
					Config.AVATAR_DIAMETER
				);
				legendCtx.fillText(
					downloadedTextInfo.userLine,
					Config.LEFT_COLUMN_WIDTH + Config.INNER_PADDING + Config.AVATAR_DIAMETER + Config.INNER_GAP / 2,
					yPosition
				);
			}
		}
		return legend;
	};

	const drawChart = (context: CanvasRenderingContext2D, chart: Chart, width: number, legendHeight: number) => {
		let chartHeightReducedBy = 0;
		if (chartType === ChartType.ColumnChart || chartType === ChartType.TrendlineChart) {
			const xAxis = chart.scales.x;
			const numberTicks = xAxis.ticks.length;
			const tickSpacing = xAxis.width / numberTicks;
			const inferredRange = BarChartConfig.CALCULATION_FUNCTIONS.inferRange(numberTicks);
			const { MAX_ROTATABLE_TABLE_WIDTH } = BarChartConfig.RANGE_SETTINGS[inferredRange];
			// try and determine if labels are rotated
			if (
				tickSpacing < BarChartConfig.TICK_SPACING_ROTATION_BREAKPOINT &&
				Config.CHART_DIMENSIONS[chartType].width - Config.CHART_DIMENSIONS[chartType].ADDITIONAL_PADDING_LEFT <=
					MAX_ROTATABLE_TABLE_WIDTH
			) {
				// if labels are rotated, reduce the height of the chart to make space for the rotated labels
				chartHeightReducedBy = Config.ROTATED_LABELS_EXTRA_SPACE;
			}
		}

		// first place the title and subtitle at the top
		const titleText = formatTitleText(context);
		context.fillStyle = getColor("primary", 9);
		context.font = `600 ${Config.FONT_SIZE}px Source Sans Pro, sans-serif`;
		const { width: titleWidth } = context.measureText(titleText);
		context.textBaseline = "bottom";
		context.fillText(
			titleText,
			Config.OUTER_PADDING + width / 2 - titleWidth / 2,
			Config.OUTER_PADDING + Config.INNER_PADDING + Config.LINE_HEIGHT
		);
		let additionalSpace = Config.LINE_HEIGHT;
		if (legendInfo.description) {
			const { width: descriptionWidth } = context.measureText(legendInfo.description || "");
			context.font = "13px 'Font Awesome 6 Pro'";
			context.fillStyle = getColor("neutral", 7);
			// add info icon to left of description
			context.fillText(
				"\uf05a",
				Config.OUTER_PADDING + width / 2 - descriptionWidth / 2,
				Config.OUTER_PADDING + Config.INNER_PADDING + Config.LINE_HEIGHT * 2 - 3 // adjust the 'i' icon
			);
			context.font = `${Config.FONT_SIZE}px Source Sans Pro, sans-serif`;
			context.fillText(
				legendInfo.description,
				Config.OUTER_PADDING + width / 2 - descriptionWidth / 2 + 20,
				Config.OUTER_PADDING + Config.INNER_PADDING + Config.LINE_HEIGHT * 2
			);
			additionalSpace += Config.LINE_HEIGHT;
		}

		const newChartWidth = Config.CHART_DIMENSIONS[chartType].width;
		const newChartHeight = Config.CHART_DIMENSIONS[chartType].height - legendHeight;

		// resize the chart itself (to rerender any customizations at the export size)
		chart.resize(
			newChartWidth - Config.CHART_DIMENSIONS[chartType].ADDITIONAL_PADDING_LEFT,
			newChartHeight + Config.CHART_DIMENSIONS[chartType].ADDITIONAL_HEIGHT_BOTTOM - chartHeightReducedBy
		);

		// draw a nice rounded rectangle around the chart
		context.beginPath();
		context.strokeStyle = getColor("neutral", 2);
		context.roundRect(
			Config.OUTER_PADDING,
			Config.OUTER_PADDING,
			newChartWidth + Config.INNER_PADDING,
			newChartHeight + Config.INNER_PADDING / 2 + additionalSpace,
			Config.BORDER_RADIUS
		);
		context.stroke();
		context.drawImage(
			chart.canvas,
			Config.OUTER_PADDING + Config.CHART_DIMENSIONS[chartType].ADDITIONAL_PADDING_LEFT,
			Config.OUTER_PADDING + Config.INNER_PADDING + additionalSpace,
			newChartWidth - Config.CHART_DIMENSIONS[chartType].ADDITIONAL_PADDING_LEFT,
			newChartHeight + Config.CHART_DIMENSIONS[chartType].ADDITIONAL_HEIGHT_BOTTOM - chartHeightReducedBy
		);
	};

	const createAndFormatExportableCanvas = (): HTMLCanvasElement | undefined => {
		const canvas = document.createElement("canvas");
		const chart = chartRef.current;
		const context = canvas.getContext("2d");
		if (chart && context) {
			const chartOriginalHeight = chart.height;
			const chartOriginalWidth = chart.width;
			canvas.width = Config.EXPORT_WIDTH;
			canvas.height = Config.EXPORT_HEIGHT;
			const sharedWidth = canvas.width - Config.OUTER_PADDING * 2;
			// add the legend to the bottom of the canvas
			const legend = createLegend(sharedWidth);
			context.fillStyle = "#fff";
			context.fillRect(0, 0, canvas.width, canvas.height);
			context.drawImage(
				legend,
				Config.OUTER_PADDING,
				canvas.height - legend.height - Config.OUTER_PADDING,
				legend.width,
				legend.height
			);
			// draw the chart onto the fresh canvas
			drawChart(context, chart, sharedWidth, legend.height);
			// reset the chart to its original size
			chart.resize(chartOriginalWidth, chartOriginalHeight);
		}
		return canvas;
	};
	// function to download the chart as an image
	const downloadImage = () => {
		const downloadable = createAndFormatExportableCanvas()?.toDataURL("image/png");
		if (downloadable) {
			const link = document.createElement("a");
			link.href = downloadable;
			//<Report-Name>-<Chart-Title>-<Date-Created>.png
			link.download = `${legendInfo.reportName}-${chartType}-${new Date().toLocaleDateString()}.png`;
			link.click();
		}
	};

	return { downloadImage, legendInfo };
};
